import React, { useState, useEffect } from "react";
import axios from "axios";
import DeleteButton from "../../../components/common/buttons/DeleteButton";
import LoadingIndicator from "../../../components/common/loadingIndicator/LoadingIndicator";
import InputField from "../../../components/common/inputs/InputField";
import { ChevronDown, ChevronUp, FilePenLine, X } from "lucide-react";

const DestinationContent: React.FC = () => {
  const [destinations, setDestinations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [image, setImage] = useState<File | null>(null);
  const [topic, setTopic] = useState<string>("");
  const [subtopic, setSubtopic] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [editingDestination, setEditingDestination] = useState<any>(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchDestinations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDestinations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/content?type=destination`);
      setDestinations(response.data);
    } catch (error) {
      console.error("Error fetching destinations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > 1024 * 1024) {
        alert("Image size should not exceed 1MB.");
        return;
      }

      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;
        if (width > 3000 || height > 3000) {
          alert("Image dimensions should not exceed 500x300.");
          return;
        }
        setImage(file);
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("topic", topic);
    formData.append("subtopic", subtopic);
    if (image) {
      formData.append("image", image);
    }

    try {
      if (editingDestination) {
        await axios.put(
          `${API_URL}/content/${editingDestination.id}?type=destination`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        console.log("Destination updated");
      } else {
        await axios.post(`${API_URL}/content?type=destination`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        console.log("Destination created");
      }
      fetchDestinations();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error saving destination:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (destination: any) => {
    setEditingDestination(destination);
    setTopic(destination.topic);
    setSubtopic(destination.subtopic);
    setImage(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingDestination(null);
    setTopic("");
    setSubtopic("");
    setImage(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/content/${id}?type=destination`);
      console.log("Destination deleted");
      fetchDestinations();
    } catch (error) {
      console.error("Error deleting destination:", error);
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div className="p-4 border-t">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold my-4">
          Destination List ({destinations.length})
        </h3>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-md"
        >
          Add Destination +
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative max-h-[95%] my-56 overflow-y-auto">
            <button
              className="absolute top-6 right-6 w-4 text-gray-500 hover:text-gray-700"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              <X />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {editingDestination ? "Edit Destination" : "Add New Destination"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                type="text"
                placeholder="Topic"
                value={topic}
                onChange={setTopic}
                label="Topic"
              />
              <InputField
                type="text"
                placeholder="Subtopic"
                value={subtopic}
                onChange={setSubtopic}
                label="Subtopic"
              />
              <div>
                <label className="block text-sm text-gray-700">
                  Upload Image <span className="text-gray-400 text-xs">(640x480 Recommended)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="border p-2 w-full rounded-md"
                />
                {editingDestination?.imageUrl && !image && (
                  <div className="mt-2">
                    <img
                      src={editingDestination.imageUrl}
                      alt="Existing Destination"
                      className="w-full h-48 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-md w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingIndicator />
                ) : editingDestination ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingIndicator />
      ) : (
        <div>
          {destinations.map((destination, index) => (
            <React.Fragment key={index}>
              <div
                className="hover:bg-gray-50 border my-2 cursor-pointer rounded-lg shadow-lg"
                onClick={() => toggleExpand(index)}
              >
                <div className="p-2 flex justify-between items-center">
                  <div className="flex flex-col col-span-3">
                    <div className="font-bold pb-2">{destination.topic}</div>
                    <div className="text-sm">{destination.subtopic}</div>
                  </div>
                  <div className="flex gap-2 justify-center items-center ">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(destination);
                      }}
                      className="text-indigo-600 mb-1 "
                    >
                      <FilePenLine size={17} />
                    </button>
                    <DeleteButton
                      onConfirm={() => handleDelete(destination.id)}
                    />
                  </div>
                </div>
                <div className="p-2 flex justify-between items-center border-t">
                  <span className="text-blue-500 text-sm">
                    {expandedIndex === index ? "Hide Details" : "Show Details"}
                  </span>
                  {expandedIndex === index ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </div>
              </div>
              {expandedIndex === index && (
                <div className="bg-gray-100 flex ">
                  <div className="p-2 w-1/3 h-1/2">
                    {destination.imageUrl ? (
                      <img
                        src={destination.imageUrl}
                        alt="Destination"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      "No Image"
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default DestinationContent;
